import type { ProfileData } from "@/queries/use_profile.ts";
import type { Option } from "@sprout/select";
import type { Employment } from "@components/quick_apply/employments";
import type { Education } from "@components/quick_apply/education_section";

export type ChecklistStatus = "complete" | "partial" | "not_started";

export interface ChecklistItem {
  key: string;
  status: ChecklistStatus;
  value?: string;
}

const isPresent = <T>(value: T): value is NonNullable<T> =>
  value !== undefined && value !== null && value !== "";

type Reader<T> = (source: T) => unknown;

type FieldsItem = {
  kind: "fields";
  key: string;
  rule: "all" | "any";
  required: Reader<ProfileData>[];
  extra?: Reader<ProfileData>[];
  format?: (data: ProfileData) => string | undefined;
  separator?: string;
};

type RecordsItem = {
  kind: "records";
  key: string;
  records: (data: ProfileData) => readonly unknown[];
  required: Reader<unknown>[];
  format: (record: unknown) => string | undefined;
};

type ChecklistDef = FieldsItem | RecordsItem;

const recordsItem = <R>(config: {
  key: string;
  records: (data: ProfileData) => readonly R[];
  required: Reader<R>[];
  format: (record: R) => string | undefined;
}): RecordsItem => ({
  kind: "records",
  key: config.key,
  records: config.records,
  required: config.required as Reader<unknown>[],
  format: config.format as (record: unknown) => string | undefined,
});

export const optionLabel = (
  value: string | number | undefined,
  options?: Option[],
): string | undefined =>
  isPresent(value)
    ? options?.find((option) => String(option.value) === String(value))?.label
    : undefined;

type SelfIdField = "gender" | "race" | "veteranStatus" | "disabilityStatus";

const selfIdLabel = (data: ProfileData, field: SelfIdField): string | undefined => {
  const selfId = data.selfIdentification?.value;
  const options = data.selfIdentification?.options;
  if (!selfId) return undefined;

  switch (field) {
    case "gender":
      return optionLabel(selfId.gender, options?.genderOptions);
    case "race":
      return optionLabel(selfId.race, [
        ...(options?.raceOptions ?? []),
        ...(options?.hispanicOrLatinoOptions ?? []),
      ]);
    case "veteranStatus":
      return optionLabel(selfId.veteranStatus, options?.veteranOptions);
    case "disabilityStatus":
      return optionLabel(selfId.disabilityStatus, options?.disabilityOptions);
  }
};

const CHECKLIST: ChecklistDef[] = [
  {
    kind: "fields",
    key: "resume",
    rule: "all",
    required: [(d) => d.resume],
    format: (d) => d.resume?.filename,
  },
  {
    kind: "fields",
    key: "contact_info",
    rule: "all",
    required: [
      (d) => d.firstName,
      (d) => d.lastName,
      (d) => d.phoneNumber,
      (d) => d.location?.display_name,
    ],
    extra: [(d) => d.preferredName],
    format: (d) =>
      [
        [d.firstName, d.lastName].filter(isPresent).join(" "),
        d.phoneNumber,
        d.location?.display_name,
      ]
        .filter(isPresent)
        .join(" • ") || undefined,
  },
  recordsItem<Employment>({
    key: "employment",
    records: (d) => d.employments ?? [],
    required: [
      (e) => e.company,
      (e) => e.title,
      (e) => e.startDate?.month,
      (e) => e.startDate?.year,
    ],
    format: (e) => [e.company, e.title].filter(isPresent).join(" • ") || undefined,
  }),
  recordsItem<Education>({
    key: "education",
    records: (d) => d.educations ?? [],
    required: [
      (e) => e.school?.label,
      (e) => e.degree?.label,
      (e) => e.discipline?.label,
      (e) => e.startMonth,
      (e) => e.startYear,
    ],
    format: (e) => [e.degree?.label, e.school?.label].filter(isPresent).join(", ") || undefined,
  }),
  {
    kind: "fields",
    key: "online_profiles",
    rule: "any",
    required: [
      (d) => d.websites?.linkedin,
      (d) => d.websites?.github,
      (d) => d.websites?.portfolio,
      (d) => d.websites?.website,
    ],
    format: (d) =>
      [d.websites?.linkedin, d.websites?.github, d.websites?.portfolio, d.websites?.website].find(
        isPresent,
      ),
  },
  {
    kind: "fields",
    key: "voluntary_self_id",
    rule: "all",
    separator: ", ",
    required: [
      (d) => selfIdLabel(d, "gender"),
      (d) => selfIdLabel(d, "race"),
      (d) => selfIdLabel(d, "veteranStatus"),
      (d) => selfIdLabel(d, "disabilityStatus"),
    ],
  },
  {
    kind: "fields",
    key: "work_preferences",
    rule: "all",
    separator: ", ",
    required: [
      (d) => d.workPreferences?.employmentTypes?.[0],
      (d) => d.workPreferences?.worksites?.[0],
      (d) => d.workPreferences?.location,
      (d) => d.workPreferences?.salary || undefined,
    ],
    extra: [
      (d) => d.workPreferences?.willingToRelocate ?? undefined,
      (d) => d.workPreferences?.languages?.[0],
      (d) => d.workPreferences?.desiredJobTitles?.[0],
    ],
    format: (d) =>
      [
        ...(d.workPreferences?.employmentTypes ?? []),
        ...(d.workPreferences?.worksites ?? []),
        d.workPreferences?.location,
      ]
        .filter(isPresent)
        .join(", ") || undefined,
  },
];

const evaluate = (def: ChecklistDef, data: ProfileData): ChecklistItem => {
  if (def.kind === "records") {
    const entries = def
      .records(data)
      .filter((entry) => def.required.some((read) => isPresent(read(entry))));
    if (entries.length === 0) return { key: def.key, status: "not_started" };

    const full = entries.find((entry) => def.required.every((read) => isPresent(read(entry))));
    return {
      key: def.key,
      status: full ? "complete" : "partial",
      value: full ? def.format(full) || undefined : undefined,
    };
  }

  const present = def.required.map((read) => read(data)).filter(isPresent);
  const hasExtra = def.extra ? def.extra.some((read) => isPresent(read(data))) : false;
  const complete = def.rule === "any" ? present.length > 0 : present.length === def.required.length;
  const status: ChecklistStatus =
    present.length === 0 && !hasExtra ? "not_started" : complete ? "complete" : "partial";
  const value = def.format ? def.format(data) : present.join(def.separator ?? " • ");
  return { key: def.key, status, value: value || undefined };
};

export const buildChecklistItems = (data: ProfileData): ChecklistItem[] =>
  CHECKLIST.map((def) => evaluate(def, data));
